import CtaArrow from "@/components/svg/CtaArrow";
import { asText, isFilled } from "@prismicio/client";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";
import { PrismicRichText } from "@prismicio/react";

const richTextBase = {
  strong: ({ children }) => <strong className="md">{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  hyperlink: ({ children, node }) => (
    <a href={node.data.url} className="underline underline-offset-2">
      {children}
    </a>
  ),
};

const sectionTitleComponents = {
  ...richTextBase,
  heading2: ({ children }) => <h2 className="h2-n lg:dk text-bleu">{children}</h2>,
  heading3: ({ children }) => <h3 className="h2-n lg:dk text-bleu">{children}</h3>,
};

const introComponents = {
  ...richTextBase,
  paragraph: ({ children }) => <p className="p-lg-n text-bleu">{children}</p>,
};

const highlightTitleComponents = {
  ...richTextBase,
  heading3: ({ children }) => <p className="p-xl-n md text-blanc">{children}</p>,
  heading4: ({ children }) => <p className="p-xl-n md text-blanc">{children}</p>,
};

/**
 * @param {import("@prismicio/client").Content.ProjectPresentationSliceDefaultPrimaryPresentationsItem} item
 */
const Presentation = ({ item }) => {
  if (!isFilled.keyText(item.sub_header) && !isFilled.richText(item.text)) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3.5">
      {isFilled.keyText(item.sub_header) && (
        <p className="section-text-n text-rouge">{item.sub_header}</p>
      )}
      {isFilled.richText(item.text) && (
        <PrismicRichText
          field={item.text}
          components={{
            ...richTextBase,
            paragraph: ({ children }) => <p className="p-lg-n text-bleu">{children}</p>,
          }}
        />
      )}
    </div>
  );
};

/**
 * @typedef {import("@prismicio/client").Content.ProjectPresentationSlice} ProjectPresentationSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ProjectPresentationSlice>} ProjectPresentationProps
 * @type {import("react").FC<ProjectPresentationProps>}
 */
const ProjectPresentation = ({ slice }) => {
  const {
    sub_header,
    title,
    text,
    project_title: projectTitleField,
    presentations,
    highlight_sub_header,
    highlight_title,
    highlight_text,
    picture,
    cta,
  } = slice.primary;

  // `project_title` used to be a rich text field: keep old documents rendering until they are re-saved.
  const project_title = Array.isArray(projectTitleField)
    ? asText(projectTitleField)
    : projectTitleField;
  const filledPresentations = Array.isArray(presentations)
    ? presentations.filter(
        (item) => isFilled.keyText(item.sub_header) || isFilled.richText(item.text),
      )
    : [];
  const hasHighlight =
    isFilled.keyText(highlight_sub_header) ||
    isFilled.richText(highlight_title) ||
    isFilled.richText(highlight_text);
  const hasDetails =
    isFilled.keyText(project_title) || filledPresentations.length > 0 || hasHighlight;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno bg-beige-30 padding-x py-15 lg:py-20"
    >
      <div className="framing-lg flex flex-col gap-[60px]">
        {(isFilled.keyText(sub_header) ||
          isFilled.richText(title) ||
          isFilled.richText(text)) && (
          <div className="flex max-w-[793px] flex-col gap-2.5">
            <div className="flex flex-col gap-1.5">
              {isFilled.keyText(sub_header) && (
                <p className="section-text-n text-rouge">{sub_header}</p>
              )}
              {isFilled.richText(title) && (
                <PrismicRichText field={title} components={sectionTitleComponents} />
              )}
            </div>
            {isFilled.richText(text) && (
              <PrismicRichText field={text} components={introComponents} />
            )}
          </div>
        )}

        {(isFilled.image(picture) || hasDetails) && (
          <div className="flex flex-col gap-[60px] lg:flex-row">
            {isFilled.image(picture) && (
              <div className="relative order-1 h-[289px] w-full shrink-0 overflow-hidden rounded-[20px] lg:order-2 lg:h-auto lg:w-[477px] lg:self-stretch">
                <PrismicNextImage
                  field={picture}
                  className="absolute inset-0 h-full w-full object-cover"
                  sizes="(max-width: 1024px) 100vw, 477px"
                />
                {isFilled.link(cta) && (
                  <PrismicNextLink
                    prefetch={false}
                    field={cta}
                    className="cta cta-text absolute bottom-5 right-10 w-fit bg-rouge text-blanc lg:bottom-10"
                  >
                    {cta.text}
                    <CtaArrow className="text-blanc" />
                  </PrismicNextLink>
                )}
              </div>
            )}

            {hasDetails && (
              <div className="order-2 flex min-w-0 flex-1 flex-col gap-[30px] lg:order-1">
                {isFilled.keyText(project_title) && (
                  <h3 className="project-title-n xlt leading-[30px] text-bleu lg:leading-[50px]">{project_title}</h3>
                )}

                {filledPresentations.length > 0 && (
                  <div className="flex flex-col gap-8 lg:max-w-[663px]">
                    {filledPresentations.map((item, index) => (
                      <Presentation key={`presentation-${index}`} item={item} />
                    ))}
                  </div>
                )}

                {hasHighlight && (
                  <div className="flex flex-col gap-3.5 rounded-[20px] bg-bleu p-[30px]">
                    {isFilled.keyText(highlight_sub_header) && (
                      <p className="section-text-n text-vert-50">{highlight_sub_header}</p>
                    )}
                    <div className="flex flex-col gap-3">
                      {isFilled.richText(highlight_title) && (
                        <PrismicRichText
                          field={highlight_title}
                          components={highlightTitleComponents}
                        />
                      )}
                      {isFilled.richText(highlight_text) && (
                        <PrismicRichText
                          field={highlight_text}
                          components={{
                            ...richTextBase,
                            paragraph: ({ children }) => (
                              <p className="p-lg-n text-blanc">{children}</p>
                            ),
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!isFilled.image(picture) && isFilled.link(cta) && (
          <PrismicNextLink
            prefetch={false}
            field={cta}
            className="cta cta-text w-fit bg-rouge text-blanc"
          >
            {cta.text}
            <CtaArrow className="text-blanc" />
          </PrismicNextLink>
        )}
      </div>
    </section>
  );
};

export default ProjectPresentation;
